<template>
  <div>
    <label v-if="label" class="tw-flex-none">
      <div class="tw-mb-1 tw-block tw-text-xs dark:tw-text-white">
        {{ label }}
      </div>
    </label>
    <div
      class="tw-overflow-hidden tw-rounded-md tw-border tw-bg-white focus-within:tw-border-deep-orange-500 focus-within:tw-ring-1 focus-within:tw-ring-deep-orange-500 hover:tw-ring-1 hover:tw-ring-deep-orange-500 dark:tw-bg-black"
      @click="focusEditor"
    >
      <div class="tw-flex tw-items-center tw-border-b tw-text-xs">
        <button
          data-testid="toggle-editor-heading-variant-button"
          class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-border-r tw-font-bold"
          :class="{ 'tw-text-blue-500': showHeadingVariantButtons }"
          @click="showHeadingVariantButtons = !showHeadingVariantButtons"
        >
          H
        </button>
        <button
          data-testid="toggle-editor-bold-button"
          :disabled="!!(editor && !editor.can().chain().focus().toggleBold().run())"
          :class="{
            'tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-border-r tw-font-bold': true,
            'tw-text-deep-orange-500': editor && editor.isActive('bold')
          }"
          @click="editor && editor.chain().focus().toggleBold().run()"
        >
          B
        </button>
        <button
          data-testid="toggle-editor-italic-button"
          :disabled="!!(editor && !editor.can().chain().focus().toggleItalic().run())"
          :class="{
            'tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-border-r tw-font-bold tw-italic': true,
            'tw-text-deep-orange-500': editor && editor.isActive('italic')
          }"
          @click="editor && editor.chain().focus().toggleItalic().run()"
        >
          i
        </button>
        <button
          data-testid="toggle-editor-strike-button"
          :disabled="!!(editor && !editor.can().chain().focus().toggleStrike().run())"
          :class="{
            'tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-border-r tw-line-through': true,
            'tw-text-deep-orange-500': editor && editor.isActive('strike')
          }"
          @click="editor && editor.chain().focus().toggleStrike().run()"
        >
          S
        </button>
        <button
          data-testid="toggle-editor-unordered-list-button"
          :class="{
            'tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-border-r': true,
            'tw-text-deep-orange-500': editor && editor.isActive('bulletList')
          }"
          @click="editor && editor.chain().focus().toggleBulletList().run()"
        >
          <ListBullet class="tw-h-4 tw-w-4" />
        </button>
        <button
          data-testid="toggle-editor-ordered-list-button"
          :class="{
            'tw-inline-flex tw-h-8 tw-w-8 tw-flex-col tw-items-center tw-justify-center tw-border-r tw-text-[5px] tw-font-bold tw-leading-none': true,
            'tw-text-deep-orange-500': editor && editor.isActive('orderedList')
          }"
          @click="editor && editor.chain().focus().toggleOrderedList().run()"
        >
          <div>1.</div>
          <div>2.</div>
          <div>3.</div>
        </button>
        <button
          data-testid="toggle-editor-dash-button"
          class="tw-inline-flex tw-h-8 tw-w-8 tw-flex-col tw-items-center tw-justify-center tw-border-r tw-text-[5px] tw-font-bold tw-leading-none"
          :class="{
            'tw-text-blue-500': editor && editor.isActive('horizontalRule')
          }"
          @click="editor && editor.chain().focus().setHorizontalRule().run()"
        >
          &mdash;
        </button>
        <button
          v-if="editor && !editor.isActive('link')"
          data-testid="toggle-editor-set-link-button"
          :class="{
            'tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-border-r': true,
            'tw-text-deep-orange-500': editor && editor.isActive('link')
          }"
          @click="setLink"
        >
          <Hyperlink class="tw-h-4 tw-w-4" />
        </button>
        <button
          v-else
          data-testid="toggle-editor-unset-link-button"
          :class="{
            'tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-border-r': true,
            'tw-text-deep-orange-500': editor && editor.isActive('link')
          }"
          @click="(editor as Editor).chain().focus().unsetLink().run()"
        >
          <Hyperlink class="tw-h-4 tw-w-4" />
        </button>
        <button
          data-testid="toggle-editor-text-align-variant-button"
          class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-border-r tw-font-bold"
          :class="{ 'tw-text-blue-500': showTextAlignVariantButtons }"
          @click="showTextAlignVariantButtons = !showTextAlignVariantButtons"
        >
          <TablerAlignLeft class="tw-h-4 tw-w-4" />
        </button>
      </div>
      <div v-if="showHeadingVariantButtons">
        <div class="tw-m-1 tw-inline-flex tw-divide-x tw-rounded tw-border tw-text-2xs">
          <button
            data-testid="toggle-editor-heading-1-button"
            :disabled="!!(editor && !editor.can().chain().focus().toggleHeading({ level: 1 }).run())"
            class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-font-bold"
            :class="{
              'tw-text-blue-500': editor && editor.isActive('heading', { level: 1 })
            }"
            @click="editor && editor.chain().focus().toggleHeading({ level: 1 }).run()"
          >
            h1
          </button>
          <button
            data-testid="toggle-editor-heading-2-button"
            :disabled="!!(editor && !editor.can().chain().focus().toggleHeading({ level: 2 }).run())"
            class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-font-bold"
            :class="{
              'tw-text-blue-500': editor && editor.isActive('heading', { level: 2 })
            }"
            @click="editor && editor.chain().focus().toggleHeading({ level: 2 }).run()"
          >
            h2
          </button>
          <button
            data-testid="toggle-editor-heading-3-button"
            :disabled="!!(editor && !editor.can().chain().focus().toggleHeading({ level: 3 }).run())"
            class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-font-bold"
            :class="{
              'tw-text-blue-500': editor && editor.isActive('heading', { level: 3 })
            }"
            @click="editor && editor.chain().focus().toggleHeading({ level: 3 }).run()"
          >
            h3
          </button>
          <button
            data-testid="toggle-editor-heading-4-button"
            :disabled="!!(editor && !editor.can().chain().focus().toggleHeading({ level: 4 }).run())"
            class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-font-bold"
            :class="{
              'tw-text-blue-500': editor && editor.isActive('heading', { level: 4 })
            }"
            @click="editor && editor.chain().focus().toggleHeading({ level: 4 }).run()"
          >
            h4
          </button>
          <button
            data-testid="toggle-editor-heading-5-button"
            :disabled="!!(editor && !editor.can().chain().focus().toggleHeading({ level: 5 }).run())"
            class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-font-bold"
            :class="{
              'tw-text-blue-500': editor && editor.isActive('heading', { level: 5 })
            }"
            @click="editor && editor.chain().focus().toggleHeading({ level: 5 }).run()"
          >
            h5
          </button>
          <button
            data-testid="toggle-editor-heading-6-button"
            :disabled="!!(editor && !editor.can().chain().focus().toggleHeading({ level: 6 }).run())"
            class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-font-bold"
            :class="{
              'tw-text-blue-500': editor && editor.isActive('heading', { level: 6 })
            }"
            @click="editor && editor.chain().focus().toggleHeading({ level: 6 }).run()"
          >
            h6
          </button>
        </div>
      </div>
      <div v-if="showTextAlignVariantButtons">
        <div class="tw-m-1 tw-inline-flex tw-divide-x tw-rounded tw-border tw-text-2xs">
          <button
            data-testid="toggle-editor-heading-1-button"
            :disabled="!!(editor && !editor.can().chain().focus().toggleHeading({ level: 1 }).run())"
            class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-font-bold"
            :class="{
              'tw-text-blue-500': editor && editor.isActive({ textAlign: 'left' })
            }"
            @click="editor && editor.chain().focus().setTextAlign('left').run()"
          >
            <TablerAlignLeft class="tw-h-4 tw-w-4" />
          </button>
          <button
            data-testid="toggle-editor-heading-2-button"
            :disabled="!!(editor && !editor.can().chain().focus().toggleHeading({ level: 2 }).run())"
            class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-font-bold"
            :class="{
              'tw-text-blue-500': editor && editor.isActive({ textAlign: 'center' })
            }"
            @click="editor && editor.chain().focus().setTextAlign('center').run()"
          >
            <TablerAlignCenter class="tw-h-4 tw-w-4" />
          </button>
          <button
            data-testid="toggle-editor-heading-3-button"
            :disabled="!!(editor && !editor.can().chain().focus().toggleHeading({ level: 3 }).run())"
            class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-font-bold"
            :class="{
              'tw-text-blue-500': editor && editor.isActive({ textAlign: 'right' })
            }"
            @click="editor && editor.chain().focus().setTextAlign('right').run()"
          >
            <TablerAlignRight class="tw-h-4 tw-w-4" />
          </button>
          <button
            data-testid="toggle-editor-heading-4-button"
            :disabled="!!(editor && !editor.can().chain().focus().toggleHeading({ level: 4 }).run())"
            class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-font-bold"
            :class="{
              'tw-text-blue-500': editor && editor.isActive({ textAlign: 'justify' })
            }"
            @click="editor && editor.chain().focus().setTextAlign('justify').run()"
          >
            <TablerAlignJustified class="tw-h-4 tw-w-4" />
          </button>
        </div>
      </div>
      <EditorContent
        :id="id"
        :data-testid="id"
        :editor="editor as any"
        :style="{
          'max-height': maxHeight,
          'min-height': minHeight,
          'overflow-y': 'scroll',
          'padding-left': '0.75rem',
          'padding-right': '0.75rem',
          'padding-top': '0.5rem',
          'padding-bottom': '0.5rem'
        }"
      />
    </div>
  </div>
</template>

<script lang="ts">
import CharacterCount from '@tiptap/extension-character-count';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import StarterKit from '@tiptap/starter-kit';
import { Editor, EditorContent } from '@tiptap/vue-3';
import { defineComponent } from 'vue';

import { ListBullet, Hyperlink } from './Icons';
import TablerAlignCenter from './Icons/tabler/TablerAlignCenter.vue';
import TablerAlignJustified from './Icons/tabler/TablerAlignJustified.vue';
import TablerAlignLeft from './Icons/tabler/TablerAlignLeft.vue';
import TablerAlignRight from './Icons/tabler/TablerAlignRight.vue';

export default defineComponent({
  name: 'StudioTiptap',
  components: {
    EditorContent,
    ListBullet,
    Hyperlink,
    TablerAlignLeft,
    TablerAlignCenter,
    TablerAlignRight,
    TablerAlignJustified
  },
  props: {
    modelValue: { type: String, default: '' },
    minHeight: { type: String, default: '300px' },
    maxHeight: { type: String, default: '300px' },
    maxCharacterCount: { type: Number, default: null },
    label: { type: String, default: '' },
    id: { type: String, required: true }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      editor: null as Editor | null,
      showHeadingVariantButtons: false,
      showTextAlignVariantButtons: false
    };
  },
  mounted() {
    this.editor = new Editor({
      content: this.modelValue,
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3, 4, 5, 6]
          }
        }),
        Link.configure({
          openOnClick: true,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer'
          }
        }),
        CharacterCount.configure({
          limit: this.maxCharacterCount
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph']
        })
      ],
      onUpdate: ({ editor }) => {
        this.$emit('update:modelValue', editor.getHTML());
      }
    }) as any;
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
  watch: {
    modelValue(value) {
      if (!this.editor) {
        return;
      }
      if (this.editor.getHTML() === value) {
        return;
      }
      this.editor.commands.setContent(this.modelValue, false);
    }
  },
  methods: {
    focusEditor() {
      if (this.editor) {
        this.editor.chain().focus().run();
      }
    },
    setLink() {
      if (this.editor) {
        const previousUrl = this.editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);

        // cancelled
        if (url === null) {
          return;
        }

        // empty
        if (url === '') {
          this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
          return;
        }

        // update link
        this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
      }
    }
  }
});
</script>

<style lang="scss">
.ProseMirror {
  appearance: none;

  ::placeholder {
    --tw-placeholder-opacity: 1;
    color: rgb(156 163 175 / var(--tw-placeholder-opacity));
  }

  :focus {
    outline: 2px solid transparent;
    outline-offset: 2px;
  }

  @media (min-width: 640px) {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  p {
    margin-bottom: 0.5rem !important;
  }

  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  h2 {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  h4 {
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  h5 {
    font-size: 0.875rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  h6 {
    font-size: 0.75rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  ul {
    list-style-type: disc;
    padding-left: 1.5rem;
  }

  ol {
    list-style-type: decimal;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.25rem;
  }

  a {
    text-decoration: underline;
  }

  hr {
    margin-bottom: 0.5rem;
    border-top-style: solid;
  }
}

.ProseMirror-focused {
  outline: none;
}
</style>
